import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { addProject } from "../../services/projectService";
import { uploadToCloudinary } from "../../services/cloudinaryService";
import { compressImage } from "../../services/imageCompression";

import "./AddProjects.css";

const AddProject = () => {
  const navigate = useNavigate();

  // =====================================================
  // PROJECT DATA
  // =====================================================

  const [project, setProject] = useState({
    title: "",
    slug: "",
    sport: "",
    surface: "",

    client: "",
    location: "",
    state: "",

    completionDate: "",

    courtLength: "",
    courtWidth: "",
    area: "",

    shortDescription: "",
    description: "",

    featured: false,

    seoTitle: "",
    seoDescription: "",
  });

  // =====================================================
  // MEDIA STATE
  // =====================================================

  const [coverImage, setCoverImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [video, setVideo] = useState(null);

  const [uploading, setUploading] = useState(false);

  // =====================================================
  // FORM CHANGE
  // =====================================================

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProject((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // =====================================================
  // COVER IMAGE
  // =====================================================

  const handleCoverImageChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setCoverImage(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image.");
      return;
    }

    try {
      console.log("Compressing cover image...");

      const compressedImage = await compressImage(file);

      console.log(
        "Cover image:",
        (file.size / 1024 / 1024).toFixed(2),
        "MB →",
        (compressedImage.size / 1024 / 1024).toFixed(2),
        "MB",
      );

      setCoverImage(compressedImage);
    } catch (error) {
      console.error("Cover image error:", error);
      alert(error.message || "Failed to process cover image.");
    }
  };

  // =====================================================
  // GALLERY IMAGES
  // =====================================================

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) {
      setGalleryImages([]);
      return;
    }

    try {
      console.log(`Compressing ${files.length} gallery images...`);

      const compressedImages = await Promise.all(
        files.map((file) => compressImage(file)),
      );

      setGalleryImages(compressedImages);

      console.log("Gallery images ready:", compressedImages);
    } catch (error) {
      console.error("Gallery image error:", error);

      alert(error.message || "Failed to process gallery images.");
    }
  };

  // =====================================================
  // REMOVE GALLERY IMAGE
  // =====================================================

  const removeGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
  };

  // =====================================================
  // VIDEO
  // =====================================================

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setVideo(null);
      return;
    }

    if (!file.type.startsWith("video/")) {
      alert("Please select a valid video.");
      return;
    }

    setVideo(file);

    console.log(
      "Video selected:",
      file.name,
      (file.size / 1024 / 1024).toFixed(2),
      "MB",
    );
  };

  // =====================================================
  // SUBMIT PROJECT
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (uploading) return;

    // ===================================================
    // BASIC VALIDATION
    // ===================================================

    if (!project.title.trim()) {
      alert("Please enter project title.");
      return;
    }

    if (!project.slug.trim()) {
      alert("Please enter project slug.");
      return;
    }

    if (!project.sport) {
      alert("Please select a sport.");
      return;
    }

    if (!project.surface) {
      alert("Please select a surface.");
      return;
    }

    if (!coverImage) {
      alert("Please select a cover image.");
      return;
    }

    // ===================================================
    // FILE SIZE LIMITS
    // ===================================================

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
    const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

    if (coverImage.size > MAX_IMAGE_SIZE) {
      alert("Cover image must be less than 10 MB.");
      return;
    }

    for (const image of galleryImages) {
      if (image.size > MAX_IMAGE_SIZE) {
        alert(`Gallery image "${image.name}" must be less than 10 MB.`);
        return;
      }
    }

    if (video && video.size > MAX_VIDEO_SIZE) {
      alert("Project video must be less than 100 MB.");
      return;
    }

    // ===================================================
    // START UPLOAD
    // ===================================================

    try {
      setUploading(true);

      console.log("================================");

      console.log("STARTING PROJECT UPLOAD");

      console.log("================================");

      // =================================================
      // 1. COVER IMAGE
      // =================================================

      console.log("Uploading cover image...");

      const coverImageUrl = await uploadToCloudinary(coverImage, "image");

      console.log("Cover image uploaded:", coverImageUrl);

      // =================================================
      // 2. GALLERY IMAGES
      // =================================================

      let galleryImageUrls = [];

      if (galleryImages.length > 0) {
        console.log(`Uploading ${galleryImages.length} gallery images...`);

        galleryImageUrls = await Promise.all(
          galleryImages.map(async (image, index) => {
            console.log(`Uploading gallery image ${index + 1}...`);

            const imageUrl = await uploadToCloudinary(image, "image");

            console.log(`Gallery image ${index + 1} uploaded:`, imageUrl);

            return imageUrl;
          }),
        );
      }

      // =================================================
      // 3. VIDEO
      // =================================================

      let videoUrl = "";

      if (video) {
        console.log("Uploading project video...");

        videoUrl = await uploadToCloudinary(video, "video");

        console.log("Video uploaded:", videoUrl);
      }

      // =================================================
      // 4. CREATE FINAL PROJECT
      // =================================================

      const finalProject = {
        ...project,

        coverImage: coverImageUrl,

        galleryImages: galleryImageUrls,

        video: videoUrl,
      };

      console.log("================================");

      console.log("FINAL PROJECT DATA");

      console.log("================================");

      console.log(finalProject);

      // =================================================
      // 5. SAVE TO FIRESTORE
      // =================================================

      console.log("Saving project to Firestore...");

      const savedProject = await addProject(finalProject);

      console.log("Project saved successfully:", savedProject);

      // =================================================
      // SUCCESS
      // =================================================

      alert("Project uploaded successfully!");

      navigate("/admin/projects");
    } catch (error) {
      console.error("================================");

      console.error("PROJECT UPLOAD ERROR");

      console.error("================================");

      console.error(error);

      alert(
        error.message || "Something went wrong while uploading the project.",
      );
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="add-project-page">
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="add-project-header">
        <div>
          <h1>Add New Project</h1>

          <p>Create a new completed sports construction project.</p>
        </div>

        <button
          type="button"
          className="back-btn"
          onClick={() => navigate("/admin/projects")}
          disabled={uploading}
        >
          ← Back to Projects
        </button>
      </div>

      {/* =================================================
          FORM
      ================================================= */}

      <form onSubmit={handleSubmit}>
        {/* =================================================
            PROJECT INFORMATION
        ================================================= */}

        <div className="project-section">
          <h2>Project Information</h2>

          <div className="form-grid">
            {/* TITLE */}

            <div className="form-group">
              <label>Project Title *</label>

              <input
                type="text"
                name="title"
                value={project.title}
                onChange={handleChange}
                placeholder="Premium Basketball Court"
                required
                disabled={uploading}
              />
            </div>

            {/* SLUG */}

            <div className="form-group">
              <label>Slug *</label>

              <input
                type="text"
                name="slug"
                value={project.slug}
                onChange={handleChange}
                placeholder="premium-basketball-court-delhi"
                required
                disabled={uploading}
              />
            </div>

            {/* SPORT */}

            <div className="form-group">
              <label>Sport *</label>

              <select
                name="sport"
                value={project.sport}
                onChange={handleChange}
                required
                disabled={uploading}
              >
                <option value="">Select Sport</option>

                <option value="Basketball">Basketball</option>

                <option value="Tennis">Tennis</option>

                <option value="Badminton">Badminton</option>

                <option value="Football">Football</option>

                <option value="Volleyball">Volleyball</option>

                <option value="Running Track">Running Track</option>

                <option value="Pickleball">Pickleball</option>

                <option value="Squash">Squash</option>

                <option value="Cricket">Cricket</option>
              </select>
            </div>

            {/* SURFACE */}

            <div className="form-group">
              <label>Surface *</label>

              <select
                name="surface"
                value={project.surface}
                onChange={handleChange}
                required
                disabled={uploading}
              >
                <option value="">Select Surface</option>

                <option value="Acrylic">Acrylic</option>

                <option value="PU">PU</option>

                <option value="Synthetic Turf">Synthetic Turf</option>

                <option value="Wooden">Wooden</option>

                <option value="EPDM">EPDM</option>
              </select>
            </div>

            {/* CLIENT */}

            <div className="form-group">
              <label>Client Name</label>

              <input
                type="text"
                name="client"
                value={project.client}
                onChange={handleChange}
                placeholder="Client / Organization"
                disabled={uploading}
              />
            </div>

            {/* LOCATION */}

            <div className="form-group">
              <label>Location</label>

              <input
                type="text"
                name="location"
                value={project.location}
                onChange={handleChange}
                placeholder="New Delhi"
                disabled={uploading}
              />
            </div>

            {/* STATE */}

            <div className="form-group">
              <label>State</label>

              <input
                type="text"
                name="state"
                value={project.state}
                onChange={handleChange}
                placeholder="Delhi"
                disabled={uploading}
              />
            </div>

            {/* COMPLETION DATE */}

            <div className="form-group">
              <label>Completion Date</label>

              <input
                type="date"
                name="completionDate"
                value={project.completionDate}
                onChange={handleChange}
                disabled={uploading}
              />
            </div>
          </div>
        </div>

        {/* =================================================
            DIMENSIONS
        ================================================= */}

        <div className="project-section">
          <h2>Project Dimensions</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>Length (ft)</label>

              <input
                type="number"
                name="courtLength"
                value={project.courtLength}
                onChange={handleChange}
                placeholder="94"
                disabled={uploading}
              />
            </div>

            <div className="form-group">
              <label>Width (ft)</label>

              <input
                type="number"
                name="courtWidth"
                value={project.courtWidth}
                onChange={handleChange}
                placeholder="50"
                disabled={uploading}
              />
            </div>

            <div className="form-group">
              <label>Total Area (sq.ft)</label>

              <input
                type="number"
                name="area"
                value={project.area}
                onChange={handleChange}
                placeholder="4700"
                disabled={uploading}
              />
            </div>
          </div>
        </div>

        {/* =================================================
            DESCRIPTION
        ================================================= */}

        <div className="project-section">
          <h2>Project Description</h2>

          <div className="form-group">
            <label>Short Description</label>

            <textarea
              name="shortDescription"
              rows="3"
              value={project.shortDescription}
              onChange={handleChange}
              placeholder="Short description of the project..."
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>Full Description</label>

            <textarea
              name="description"
              rows="7"
              value={project.description}
              onChange={handleChange}
              placeholder="Write the complete project description..."
              disabled={uploading}
            />
          </div>
        </div>

        {/* =================================================
            MEDIA
        ================================================= */}

        <div className="project-section">
          <h2>Project Media</h2>

          {/* COVER IMAGE */}

          <div className="upload-box">
            <div className="upload-icon">📷</div>

            <h3>Cover Image</h3>

            <p>Main image displayed for this project.</p>

            <input
              type="file"
              accept="image/*"
              onChange={handleCoverImageChange}
              disabled={uploading}
            />

            {coverImage && (
              <div className="media-preview">
                <img
                  src={URL.createObjectURL(coverImage)}
                  alt="Cover Preview"
                />
              </div>
            )}
          </div>

          {/* GALLERY */}

          <div className="upload-box">
            <div className="upload-icon">🖼️</div>

            <h3>Gallery Images</h3>

            <p>Upload multiple project images.</p>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryChange}
              disabled={uploading}
            />
          </div>

          {/* GALLERY PREVIEW */}

          {galleryImages.length > 0 && (
            <div className="gallery-preview-grid">
              {galleryImages.map((image, index) => (
                <div
                  className="gallery-preview-item"
                  key={`${image.name}-${index}`}
                >
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Gallery ${index + 1}`}
                  />

                  <button
                    type="button"
                    onClick={() => removeGalleryImage(index)}
                    disabled={uploading}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* VIDEO */}

          <div className="upload-box">
            <div className="upload-icon">🎥</div>

            <h3>Project Video</h3>

            <p>Upload the completed project video.</p>

            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              disabled={uploading}
            />

            {video && (
              <div className="video-preview">
                <video controls src={URL.createObjectURL(video)} />
              </div>
            )}
          </div>
        </div>

        {/* =================================================
            SEO
        ================================================= */}

        <div className="project-section">
          <h2>SEO Settings</h2>

          <div className="form-group">
            <label>SEO Title</label>

            <input
              type="text"
              name="seoTitle"
              value={project.seoTitle}
              onChange={handleChange}
              placeholder="Best Basketball Court Construction in Delhi"
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>SEO Description</label>

            <textarea
              name="seoDescription"
              rows="4"
              value={project.seoDescription}
              onChange={handleChange}
              placeholder="SEO description for this project..."
              disabled={uploading}
            />
          </div>
        </div>

        {/* =================================================
            FEATURED
        ================================================= */}

        <div className="project-section">
          <div className="featured-row">
            <input
              type="checkbox"
              name="featured"
              checked={project.featured}
              onChange={handleChange}
              disabled={uploading}
            />

            <div>
              <strong>Featured Project</strong>

              <p>Show this project in the featured projects section.</p>
            </div>
          </div>
        </div>

        {/* =================================================
            BUTTONS
        ================================================= */}

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => navigate("/admin/projects")}
            disabled={uploading}
          >
            Cancel
          </button>

          <button type="submit" className="save-btn" disabled={uploading}>
            {uploading ? "Uploading Project..." : "Save Project"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProject;
